/**
 * MessageRetry — retry affordance for a failed user message.
 *
 * Shown when a user message couldn't reach the assistant (connection
 * dropped, stream errored, server returned 5xx). Two layouts share one
 * voice + one disabled-while-busy contract:
 *
 *   - `inline` — slot into `<MessageRow actions={...}>`. Renders as a
 *     tiny ghost icon-button so it disappears into the bubble's hover
 *     row. Use when the failed message and its retry sit close to other
 *     hover-revealed message actions (copy / edit).
 *   - `block` — render directly below the failed `<MessageRow>`. A
 *     destructive-tinted helper line with the failure reason on the
 *     left and a "Send again" pill on the right. Use when the failure
 *     itself is the dominant signal and the user's eyes are on the
 *     bottom of the chat.
 *
 * The primitive does not own retry mechanics. It calls `onRetry()` and
 * lets the consumer decide what "retry" means: prefill the input
 * (`onRetry` returns the failed message text and the consumer drops it
 * into `<ChatInput value=...>`), or fire the network call directly.
 *
 * Loading state: pass `isRetrying` to swap the icon for a `<Spinner>`
 * and force-disable the button. Reduced-motion users get the spinner
 * without animation per the existing `Spinner` primitive.
 *
 * Press: both layouts are pill-shaped (`rounded-full`), so they keep the
 * round Onyx "offset key" treatment — a springy 1px press-DOWN on `:active`
 * with the shared overshoot ease, the same bounce every Button/IconButton
 * uses, so the whole lib presses consistently. Round controls stay round:
 * no hex notch, no travel block, no keyline ring (those are for rectangular
 * key faces). The old Framer `whileTap` scale-tap is gone in favour of the
 * CSS press; `motion={false}` still opts out of the press ANIMATION.
 */
import * as React from "react";
import { RotateCcw, AlertCircle } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { Spinner } from "../primitives/Spinner";
import { useReducedMotion } from "../hooks/useReducedMotion";

/* ============================================================================
 * Variants
 * ========================================================================= */

/**
 * The shared offset-key press for this round/pill control: a springy 1px
 * press-DOWN with the overshoot ease — the same bounce every Button /
 * IconButton uses. Trails the variant classes so the transform transition
 * wins over the base `transition-all` color transition.
 */
const RETRY_PRESS =
  "transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)] active:translate-y-px";

const messageRetryVariants = cva(
  [
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium",
    "ring-offset-background transition-all duration-180 ease-smooth-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      layout: {
        // Hover-row companion. No background, no padding bloat — fits
        // alongside copy / edit icon-buttons on the same baseline.
        inline: cn(
          "h-7 px-2 text-xs text-muted-foreground",
          "hover:text-foreground hover:bg-foreground/5",
          "[&_svg]:size-3.5",
        ),
        // Standalone destructive-tinted pill. Sits below the failed
        // message row; the destructive tint makes the failure the
        // signal, not just a quiet hover affordance.
        block: cn(
          "h-8 px-3 text-xs",
          "border border-destructive/30 bg-destructive/5 text-destructive",
          "hover:bg-destructive/10",
          "[&_svg]:size-3.5",
        ),
      },
    },
    defaultVariants: {
      layout: "inline",
    },
  },
);

/* ============================================================================
 * Component
 * ========================================================================= */

type ButtonElementProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag" | "onClick"
>;

export interface MessageRetryProps
  extends ButtonElementProps,
    VariantProps<typeof messageRetryVariants> {
  /**
   * Fired when the user wants to retry. Consumer decides what retry
   * means (re-send the request, prefill the input, etc.). The failed
   * message text isn't passed in — the consumer already holds it.
   */
  onRetry: () => void;
  /**
   * Loading flag for the in-flight retry. Forces `disabled`, sets
   * `aria-busy`, swaps the icon for a `<Spinner>`. Default `false`.
   */
  isRetrying?: boolean;
  /**
   * Short reason rendered next to the icon in the `block` layout.
   * Falls back to "Couldn't send" so the bubble always shows
   * *something* when the connection drops without a structured error.
   * Ignored in the `inline` layout.
   */
  reason?: string;
  /** Override the button label. Default "Send again". */
  label?: string;
  /** Disable the press springback. Default true. */
  motion?: boolean;
}

export const MessageRetry = React.forwardRef<HTMLButtonElement, MessageRetryProps>(
  (
    {
      className,
      layout = "inline",
      onRetry,
      isRetrying = false,
      reason,
      label = "Send again",
      motion: enableMotion = true,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const shouldAnimate = enableMotion && reduced !== true;

    const button = (
      <button
        ref={ref}
        className={cn(
          messageRetryVariants({ layout }),
          // The springy press-DOWN (gated by `motion` + reduced-motion); trails
          // the variant classes so the transform transition wins over the base
          // `transition-all` color transition.
          shouldAnimate && RETRY_PRESS,
          className,
        )}
        onClick={onRetry}
        disabled={disabled || isRetrying}
        aria-busy={isRetrying || undefined}
        type={type}
        {...rest}
      >
        {isRetrying ? (
          <Spinner size="sm" decorative />
        ) : (
          <RotateCcw aria-hidden />
        )}
        <span>{label}</span>
      </button>
    );

    if (layout === "inline") {
      return button;
    }

    return (
      <div
        role="status"
        className="flex items-center justify-between gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2"
      >
        <span className="flex items-center gap-2 text-xs text-destructive min-w-0">
          <AlertCircle className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{reason || "Couldn't send"}</span>
        </span>
        {button}
      </div>
    );
  },
);
MessageRetry.displayName = "MessageRetry";

export { messageRetryVariants };
